import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { objectsAPI, liveAPI, reportsAPI, statusAPI, notesAPI, selfNotesAPI, shiftsAPI } from '@/lib/api';
import { geolocationService } from '@/services/geolocation';
import { notificationService } from '@/services/notifications';
import { formatDuration, formatDurationShort, getMonthStartDate, getMonthEndDate } from '@/lib/utils';
import { 
  Building2, 
  Clock, 
  AlertTriangle, 
  Play, 
  Pause, 
  UtensilsCrossed,
  MapPin,
  Loader2,
  RefreshCw,
  MapPinOff,
  Bell,
  BellOff,
  TrendingUp,
  Users,
  Zap,
  MessageSquare,
  PenLine,
  Camera,
  X,
  Trash2,
  Image as ImageIcon,
  ChevronDown,
  ChevronUp,
  Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

async function compressImage(file, maxPx = 1920, quality = 0.82) {
  return new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob(blob => resolve(blob || file), 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

export default function Dashboard() {
  const { user, isAdmin, isHR, isController, isEmployee } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [myStatus, setMyStatus] = useState(null);
  const [myHours, setMyHours] = useState([]);
  const [myNotes, setMyNotes] = useState([]); // legacy, kept for compatibility
  const [mySelfNotes, setMySelfNotes] = useState([]); // legacy
  const [notesFeed, setNotesFeed] = useState([]);
  const [notesSkip, setNotesSkip] = useState(0);
  const [notesHasMore, setNotesHasMore] = useState(false);
  const [statusDialog, setStatusDialog] = useState({ open: false, title: '', employees: [] });
  const [liveData, setLiveData] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [gpsEnabled, setGpsEnabled] = useState(false);
  const [gpsPermission, setGpsPermission] = useState('unknown');
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [isTracking, setIsTracking] = useState(false);
  const [myObjects, setMyObjects] = useState([]);
  const [todayLog, setTodayLog] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [myShifts, setMyShifts] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    const initServices = async () => {
      if (geolocationService.isAvailable()) {
        const permission = await geolocationService.checkPermission();
        setGpsPermission(permission);
        setGpsEnabled(permission !== 'denied');
      }
      if (notificationService.checkSupport()) {
        const enabled = await notificationService.hasPermission();
        setNotificationsEnabled(enabled);
        try { await notificationService.prepare(); } catch (e) {}
      }
    };
    initServices();
  }, []);

  const startTracking = useCallback(async () => {
    const started = await geolocationService.startTracking(
      (status) => {
        setMyStatus(status);
        if (status.has_violation && notificationsEnabled) {
          notificationService.showViolationNotification({ description: status.violation_reason });
        }
      },
      (error) => toast.error(error.message)
    );
    setIsTracking(!!started);
  }, [notificationsEnabled]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isEmployee && gpsEnabled && !isTracking) {
      startTracking();
    }
  }, [isEmployee, gpsEnabled, isTracking, startTracking]);

  useEffect(() => {
    if (!isEmployee) return;
    const handleVisibilityChange = () => {
      if (document.hidden && isTracking) {
        toast.warning(t('dashboard.gpsTabWarning') || 'GPS is active — keep this tab open for accurate tracking', { duration: 6000, id: 'gps-tab-warning' });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isEmployee, isTracking, t]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [isEmployee, isAdmin, isHR]);

  useEffect(() => {
    if (myStatus?.status_started_at) {
      const updateTimer = () => {
        const started = new Date(myStatus.status_started_at);
        setTimer(Math.floor((new Date() - started) / 1000));
      };
      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    }
  }, [myStatus?.status_started_at]);

  const loadData = async () => {
    try {
      if (isEmployee) {
        const [statusRes, hoursRes, notesRes, objRes, logRes, shiftsRes] = await Promise.all([
          statusAPI.current(),
          reportsAPI.myHours(getMonthStartDate(), getMonthEndDate()),
          notesAPI.myFeed(0, 10),
          statusAPI.myObjects(),
          statusAPI.todayLog(),
          shiftsAPI.my().catch(() => ({ data: [] }))
        ]);
        setMyStatus(statusRes.data);
        setMyHours(hoursRes.data);
        const feedItems = notesRes.data || [];
        setNotesFeed(feedItems);
        setNotesSkip(feedItems.length);
        setNotesHasMore(feedItems.length === 10);
        setMyObjects(objRes.data?.objects || []);
        setTodayLog(logRes.data || null);
        const shiftsData = shiftsRes.data?.shifts || [];
        setMyShifts(shiftsData);
      } else {
        const [objectsRes, liveRes] = await Promise.all([
          objectsAPI.list(),
          isAdmin || isHR ? liveAPI.all() : Promise.resolve({ data: [] })
        ]);
        
        let totalWorking = 0, totalPause = 0, totalLunch = 0, totalOutside = 0, totalViolations = 0;
        liveRes.data.forEach(obj => {
          totalWorking += obj.total_working || 0;
          totalPause += obj.total_pause || 0;
          totalLunch += obj.total_lunch || 0;
          totalOutside += obj.total_outside || 0;
          totalViolations += obj.total_violations || 0;
        });

        setStats({ totalObjects: objectsRes.data.length, totalWorking, totalPause, totalLunch, totalOutside, totalViolations });
        setLiveData(liveRes.data);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const requestGpsPermission = async () => {
    try {
      await geolocationService.requestPermission();
      setGpsEnabled(true);
      setGpsPermission('granted');
      toast.success('Location access granted');
      startTracking();
    } catch (error) {
      toast.error('Location access denied');
    }
  };


  const deleteNote = async (noteId) => {
    try {
      await notesAPI.delete(noteId);
      setNotesFeed(prev => prev.filter(n => n.id !== noteId));
      toast.success(t('employee.noteDeleted'));
    } catch {
      toast.error(t('employee.noteDeleteError'));
    }
  };

  const loadMoreNotes = async () => {
    try {
      const res = await notesAPI.myFeed(notesSkip, 10);
      const items = res.data || [];
      setNotesFeed(prev => [...prev, ...items]);
      setNotesSkip(prev => prev + items.length);
      setNotesHasMore(items.length === 10);
    } catch {
      toast.error('Failed to load more notes');
    }
  };

  const openStatusDialog = (key) => {
    const employees = (liveData || []).flatMap(obj =>
      (obj.employees || []).filter(emp => {
        if (key === 'violation') return !!emp.has_violation;
        return emp.status === key;
      }).map(emp => ({
        ...emp,
        object_name: obj.object_name,
      }))
    );

    const titles = {
      working: t('status.working'),
      pause: t('status.onPause'),
      lunch: t('status.atLunch'),
      outside: t('status.outside'),
      violation: t('dashboard.violations'),
    };

    setStatusDialog({
      open: true,
      title: titles[key] || key,
      employees,
    });
  };


  const requestNotificationPermission = async () => {
    try {
      const granted = await notificationService.initialize();
      if (granted) {
        setNotificationsEnabled(true);
        toast.success(t('employee.notificationsEnabled'));
      } else {
        toast.error(t('employee.notificationsFailed'));
      }
    } catch (error) {
      toast.error(t('employee.notificationsFailed'));
    }
  };

  const updateStatus = async (newStatus) => {
    setStatusLoading(true);
    try {
      const result = await geolocationService.changeStatus(newStatus);
      if (result) setMyStatus(result);
    } catch (error) {
      const msg = error?.response?.data?.detail || t('employee.statusUpdateFailed');
      toast.error(msg);
    } finally {
      setStatusLoading(false);
    }
  };

  const refreshStatus = async () => {
    setStatusLoading(true);
    try {
      const position = await geolocationService.getCurrentPosition();
      // Let server auto-detect the object by geofence (checks today's shift first, then all assigned objects)
      const result = await geolocationService.sendStatusUpdate(position, {});
      if (result) { setMyStatus(result); toast.success(t('employee.locationUpdated')); }
    } catch (error) {
      try {
        const result = await geolocationService.sendNoGpsStatus();
        if (result) setMyStatus(result);
      } catch (e) {
        toast.error('Failed to update status');
      }
    } finally {
      setStatusLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="relative">
          <Loader2 className="h-10 w-10 animate-spin text-wp-medium" />
          <div className="absolute inset-0 blur-xl bg-wp-medium/30 animate-pulse" />
        </div>
      </div>
    );
  }

  // Employee Dashboard
  if (isEmployee) {
    const totalWorkingSeconds = myHours.reduce((acc, h) => acc + (h.working_seconds || 0), 0);
    // Rules come from the object where employee is GPS-detected (myStatus.object_id)
    const currentObj = myObjects.find(o => o.id === myStatus?.object_id);
    const pauseEnabled = currentObj?.rules?.pause_rules?.enabled !== false;
    const lunchEnabled = currentObj?.rules?.lunch_rules?.enabled !== false;
    const shiftEnabled = myShifts.length > 0;
    const statusColors = {
      working: 'from-emerald-500 to-emerald-600',
      pause: 'from-amber-500 to-amber-600',
      lunch: 'from-wp-light to-wp-medium',
      outside: 'from-wp-dark to-wp-darker',
      no_gps: 'from-rose-500 to-rose-600'
    };
    
    return (
      <div className="space-y-5 md:space-y-8 pb-24 md:pb-8" data-testid="employee-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-4xl font-bold text-white font-heading">
            {t('dashboard.welcome')}, <span className="gradient-text">{user?.name?.split(' ')[0]}</span>
          </h1>
          <p className="text-white/40 mt-1">{t('dashboard.yourDashboard')}</p>
        </div>

        {/* My Shift Today + Interactive strip â€” Ð¿Ð¾ÐºÐ°Ð·Ñ‹Ð²Ð°ÐµÐ¼ ÐµÑÐ»Ð¸ ÐµÑÑ‚ÑŒ ÑÐ¼ÐµÐ½Ñ‹ */}
        {shiftEnabled && (() => {
          const today = new Date().toISOString().slice(0, 10);
          const todayShift = myShifts.find(s => s.date === today);
          const weekDays = Array.from({ length: 7 }, (_, i) => {
            const d = new Date(); d.setDate(d.getDate() + i);
            return d.toISOString().slice(0, 10);
          });
          return (
            <div className="glass-card rounded-2xl p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-wp-medium/20 flex items-center justify-center">
                  <Clock className="h-5 w-5 text-wp-light" />
                </div>
                <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider">{t('schedule.myShiftToday')}</h3>
              </div>
              {todayShift ? (
                <p className="text-2xl font-light text-white mb-3">
                  {todayShift.start_time} â€“ {todayShift.end_time}
                  {todayShift.object_name && <span className="ml-3 text-sm text-white/40">{todayShift.object_name}</span>}
                </p>
              ) : (
                <p className="text-white/30 mb-3 text-sm">{t('schedule.noShiftToday')}</p>
              )}

              {/* 7-day strip */}
              <div className="flex gap-1.5 md:gap-2 overflow-x-auto">
                {weekDays.map(d => {
                  const s = myShifts.find(x => x.date === d);
                  const dayNum = parseInt(d.slice(8));
                  const isToday = d === today;
                  const isSelected = d === selectedDay;
                  return (
                    <button
                      key={d}
                      onClick={() => setSelectedDay(d === selectedDay ? null : d)}
                      className="flex flex-col items-center gap-1 flex-1"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all ${isSelected ? 'ring-2 ring-wp-light' : ''} ${isToday ? 'bg-wp-medium text-white' : s ? 'bg-wp-medium/20 text-wp-light' : 'bg-white/5 text-white/20'}`}>
                        {dayNum}
                      </div>
                      <div className={`w-1.5 h-1.5 rounded-full ${s ? 'bg-wp-light' : 'bg-transparent'}`} />
                    </button>
                  );
                })}
              </div>

              {/* Selected day details */}
              {selectedDay && (
                <div className="mt-3 pt-3 border-t border-white/10">
                  <p className="text-xs text-white/50 mb-2">
                    {new Date(selectedDay + 'T00:00:00').toLocaleDateString(
                      language === 'ru' ? 'ru-RU' : language === 'et' ? 'et-EE' : 'en-US',
                      { weekday: 'long', day: 'numeric', month: 'long' }
                    )}
                  </p>
                  {myShifts.filter(s => s.date === selectedDay).length > 0
                    ? myShifts.filter(s => s.date === selectedDay).map((sh, i) => (
                        <div key={i} className={`flex gap-2 items-center py-1.5 ${i > 0 ? 'border-t border-white/5' : ''}`}>
                          <span className="font-medium text-sm text-white">{sh.start_time} â€“ {sh.end_time}</span>
                          {sh.object_name && <span className="text-xs text-white/40">â€¢ {sh.object_name}</span>}
                        </div>
                      ))
                    : <p className="text-xs text-white/30">{t('schedule.dayOff')}</p>
                  }
                  <Link to="/my-schedule" className="block mt-2 text-xs text-wp-light hover:text-white transition-colors">
                    {t('schedule.viewFullSchedule')} â†’
                  </Link>
                </div>
              )}
            </div>
          );
        })()}

        {/* Install & GPS hint banner â€” shown only on mobile when GPS is active but Wake Lock not available */}
        {gpsEnabled && typeof window !== 'undefined' && !('wakeLock' in navigator) && (
          <div className="glass-card rounded-2xl p-4 border border-amber-500/20 bg-amber-500/5">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center shrink-0 mt-0.5">
                <Zap className="h-4 w-4 text-amber-400" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-amber-300 text-sm">{t('employee.gpsBackgroundTip')}</p>
                <p className="text-xs text-white/40 mt-1">{t('employee.gpsBackgroundHint')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Permission Cards */}
        {(!gpsEnabled || !notificationsEnabled) && (
          <div className="space-y-3">
            {!gpsEnabled && (
              <div className="glass-card rounded-2xl p-4 border-l-4 border-amber-500">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0">
                    <MapPinOff className="h-5 w-5 text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white text-sm">{t('employee.gpsRequired')}</p>
                    <p className="text-xs text-wp-light/40 truncate">{t('employee.enableGps')}</p>
                  </div>
                  <button 
                    onClick={requestGpsPermission}
                    className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-medium rounded-xl transition-all text-sm shrink-0"
                    data-testid="enable-gps-button"
                  >
                    {t('employee.enable')}
                  </button>
                </div>
              </div>
            )}
            
            {gpsEnabled && !notificationsEnabled && (
              <div className="glass-card rounded-2xl p-4 border-l-4 border-wp-light">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-wp-light/20 flex items-center justify-center shrink-0">
                    <BellOff className="h-5 w-5 text-wp-light" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-white text-sm">{t('employee.enableNotifications')}</p>
                    <p className="text-xs text-wp-light/40 truncate">{t('employee.notificationInfo')}</p>
                  </div>
                  <button 
                    onClick={requestNotificationPermission}
                    className="px-4 py-2 bg-wp-dark/30 hover:bg-wp-dark/50 text-white font-medium rounded-xl transition-all border border-wp-dark/50 text-sm shrink-0"
                    data-testid="enable-notifications-button"
                  >
                    {t('employee.enable')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* No objects assigned warning */}
        {user?.assigned_objects?.length === 0 && (
          <div className="glass-card rounded-2xl p-5 border-l-4 border-amber-500/50 bg-amber-500/5">
            <div className="flex items-center gap-4">
              <AlertTriangle className="h-6 w-6 text-amber-400 shrink-0" />
              <div>
                <p className="font-semibold text-amber-300">{t('employee.noObjectsAssigned')}</p>
                <p className="text-sm text-white/40">{t('employee.noObjectsHint')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Status Indicators */}
        <div className="flex items-center gap-4 text-xs flex-wrap">
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${gpsEnabled ? 'bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]' : 'bg-rose-500'}`} />
            <span className="text-wp-light/50">{gpsEnabled ? t('employee.gpsActive') : t('employee.gpsOff')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${isTracking ? 'bg-emerald-500 animate-pulse' : 'bg-wp-light/20'}`} />
            <span className="text-wp-light/50">{isTracking ? t('employee.trackingOn') : t('employee.trackingOff')}</span>
          </div>
          {notificationsEnabled && (
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-wp-light" />
              <span className="text-wp-light/50">{t('employee.notificationsOn')}</span>
            </div>
          )}
        </div>

        {/* Main Status Card */}
        <div className="glass-card rounded-2xl md:rounded-3xl p-5 md:p-8 relative overflow-hidden">
          {/* Glow effect */}
          <div className={`absolute -top-20 -right-20 w-64 h-64 rounded-full blur-[100px] opacity-30 bg-gradient-to-br ${statusColors[myStatus?.status] || statusColors.outside}`} />
          
          <div className="relative flex flex-col items-center space-y-6">
            {/* Refresh button */}
            <button 
              onClick={refreshStatus} 
              disabled={statusLoading}
              className="absolute top-0 right-0 p-2 text-white/30 hover:text-white/70 transition-colors"
            >
              <RefreshCw className={`h-5 w-5 ${statusLoading ? 'animate-spin' : ''}`} />
            </button>

            {/* Status circle */}
            <div className={`status-indicator-lg ${myStatus?.status || 'outside'}`}>
              {myStatus?.status === 'working' && <Play className="h-8 w-8 md:h-12 md:w-12 text-white" />}
              {myStatus?.status === 'pause' && <Pause className="h-8 w-8 md:h-12 md:w-12 text-white" />}
              {myStatus?.status === 'lunch' && <UtensilsCrossed className="h-8 w-8 md:h-12 md:w-12 text-white" />}
              {myStatus?.status === 'outside' && <MapPin className="h-8 w-8 md:h-12 md:w-12 text-white" />}
              {myStatus?.status === 'no_gps' && <AlertTriangle className="h-8 w-8 md:h-12 md:w-12 text-white" />}
            </div>
            
            {/* Status label */}
            <div className={`px-6 py-2 rounded-full bg-gradient-to-r ${statusColors[myStatus?.status] || statusColors.outside} text-white font-semibold uppercase tracking-wider text-sm`}>
              {myStatus?.status ? (t(`status.${myStatus.status}`) || myStatus.status.replace('_', ' ')) : t('status.outside')}
            </div>
            
            {/* Timer */}
            <div className="timer-display text-white" data-testid="status-timer">
              {formatDuration(timer)}
            </div>

            {/* Object name â€” prominent */}
            {myStatus?.object_name && (
              <div className="flex flex-col items-center gap-0.5">
                <p className="text-white/30 text-xs uppercase tracking-widest">{t('employee.currentObject') || 'Object'}</p>
                <p className="text-white text-lg md:text-xl font-bold text-center leading-tight">{myStatus.object_name}</p>
              </div>
            )}

            {/* Violation alert */}
            {myStatus?.has_violation && (
              <div className="px-6 py-3 bg-rose-500/20 border border-rose-500/30 text-rose-400 rounded-xl text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                {myStatus.violation_reason}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        {(pauseEnabled || lunchEnabled) && (
          <div className={`grid gap-3 ${pauseEnabled && lunchEnabled ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {pauseEnabled && (
              <button
                onClick={() => updateStatus('pause')}
                disabled={statusLoading || myStatus?.status === 'pause' || myStatus?.status === 'outside' || myStatus?.status === 'no_gps'}
                className="h-16 md:h-20 rounded-xl md:rounded-2xl bg-gradient-to-r from-amber-500/20 to-amber-600/20 border border-amber-500/30 text-amber-400 font-semibold text-base md:text-lg flex items-center justify-center gap-2 md:gap-3 hover:from-amber-500/30 hover:to-amber-600/30 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="pause-button"
              >
                <Pause className="h-5 w-5 md:h-6 md:w-6" />
                {t('employee.pause')}
              </button>
            )}
            {lunchEnabled && (
              <button
                onClick={() => updateStatus('lunch')}
                disabled={statusLoading || myStatus?.status === 'lunch' || myStatus?.status === 'outside' || myStatus?.status === 'no_gps'}
                className="h-16 md:h-20 rounded-xl md:rounded-2xl bg-gradient-to-r from-cyan-500/20 to-cyan-600/20 border border-cyan-500/30 text-cyan-400 font-semibold text-base md:text-lg flex items-center justify-center gap-2 md:gap-3 hover:from-cyan-500/30 hover:to-cyan-600/30 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="lunch-button"
              >
                <UtensilsCrossed className="h-5 w-5 md:h-6 md:w-6" />
                {t('employee.lunch')}
              </button>
            )}
          </div>
        )}

        {myStatus?.status !== 'working' && myStatus?.status !== 'outside' && myStatus?.status !== 'no_gps' && (
          <button
            onClick={() => updateStatus('working')}
            disabled={statusLoading}
            className="w-full h-16 md:h-20 rounded-xl md:rounded-2xl bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold text-base md:text-lg flex items-center justify-center gap-2 md:gap-3 hover:from-emerald-400 hover:to-emerald-500 transition-all shadow-[0_0_30px_rgba(16,185,129,0.3)] disabled:opacity-50"
            data-testid="resume-button"
          >
            <Play className="h-5 w-5 md:h-6 md:w-6" />
            {t('employee.resume')}
          </button>
        )}

        {/* Monthly Stats */}
        <div className="glass-card rounded-2xl p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <Clock className="h-7 w-7 text-emerald-400" />
              </div>
              <div>
                <p className="text-white/40 text-sm">{t('dashboard.thisMonth')}</p>
                <p className="text-3xl font-bold text-emerald-400 font-heading" data-testid="monthly-hours">
                  {formatDurationShort(totalWorkingSeconds)}
                </p>
              </div>
            </div>
            <TrendingUp className="h-8 w-8 text-emerald-500/30" />
          </div>
        </div>

        {/* Today's Summary */}
        {todayLog && (
          <div className="glass-card rounded-2xl p-4 md:p-6">
            <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-4">{t('employee.todayHistory') || "Today's summary"}</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="flex flex-col items-center gap-1 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                <Play className="h-4 w-4 text-emerald-400" />
                <span className="text-lg font-bold text-emerald-300">{formatDurationShort(todayLog.today_working_seconds || 0)}</span>
                <span className="text-xs text-white/40">{t('employee.todayWork') || 'Work'}</span>
              </div>
              <div className="flex flex-col items-center gap-1 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <Pause className="h-4 w-4 text-amber-400" />
                <span className="text-lg font-bold text-amber-300">{formatDurationShort(todayLog.today_pause_seconds || 0)}</span>
                <span className="text-xs text-white/40">{t('employee.todayPause') || 'Pauses'}</span>
              </div>
              <div className="flex flex-col items-center gap-1 p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                <UtensilsCrossed className="h-4 w-4 text-cyan-400" />
                <span className="text-lg font-bold text-cyan-300">{formatDurationShort(todayLog.today_lunch_seconds || 0)}</span>
                <span className="text-xs text-white/40">{t('employee.todayLunch') || 'Lunch'}</span>
              </div>
            </div>

            {/* History toggle */}
            {todayLog.entries && todayLog.entries.length > 0 && (
              <div className="mt-4">
                <button
                  onClick={() => setShowHistory(h => !h)}
                  className="flex items-center gap-2 text-sm text-white/40 hover:text-white/70 transition-colors"
                >
                  {showHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  {showHistory ? (t('employee.hideHistory') || 'Hide history') : (t('employee.showHistory') || 'Show history')}
                </button>

                {showHistory && (
                  <div className="mt-3 space-y-1">
                    {todayLog.entries.map((entry, idx) => {
                      const isActive = !entry.ended_at;
                      const statusIcons = { working: Play, pause: Pause, lunch: UtensilsCrossed, outside: MapPin, no_gps: AlertTriangle };
                      const statusColors2 = { working: 'text-emerald-400', pause: 'text-amber-400', lunch: 'text-cyan-400', outside: 'text-white/30', no_gps: 'text-rose-400' };
                      const StatusIcon = statusIcons[entry.status] || MapPin;
                      const timeStr = entry.started_at
                        ? new Date(entry.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
                        : '--:--';
                      return (
                        <div key={idx} className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${isActive ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-white/3'}`}>
                          <span className="text-white/30 w-12 shrink-0 font-mono text-xs">{timeStr}</span>
                          <StatusIcon className={`h-3.5 w-3.5 shrink-0 ${statusColors2[entry.status] || 'text-white/30'}`} />
                          <span className={`flex-1 capitalize ${isActive ? 'text-white' : 'text-white/50'}`}>{entry.status?.replace('_', ' ')}</span>
                          {entry.object_name && <span className="text-white/30 text-xs truncate max-w-[80px]">{entry.object_name}</span>}
                          <span className={`text-xs shrink-0 ${isActive ? 'text-emerald-400 font-medium' : 'text-white/30'}`}>
                            {isActive ? (t('employee.active') || 'Active') : formatDurationShort(entry.duration_seconds || 0)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Navigation shortcuts */}
        <div className="space-y-2">
          <button
            onClick={() => navigate('/my-history')}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 transition-colors text-sm text-white/50 hover:text-white/80"
          >
            <TrendingUp className="h-4 w-4" />
            {t('nav.myHistory') || 'My history'}
          </button>
          <button
            onClick={() => navigate('/my-notes')}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 transition-colors text-sm text-white/50 hover:text-white/80"
          >
            <MessageSquare className="h-4 w-4" />
            {t('nav.myNotes') || 'Notes'}
          </button>
        </div>
      </div>
    );
  }

  // Admin/HR/Controller Dashboard
  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold text-white font-heading">{t('dashboard.title')}</h1>
          <p className="text-wp-light/40 mt-1">{t('dashboard.presenceOverview')}</p>
        </div>
        <button 
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2 md:px-5 md:py-2.5 bg-wp-dark/30 hover:bg-wp-dark/50 text-wp-light/70 hover:text-wp-lightest rounded-xl border border-wp-dark/50 transition-all text-sm"
        >
          <RefreshCw className="h-4 w-4" />
          <span className="hidden sm:inline">{t('common.refresh')}</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { label: t('dashboard.objects'), value: stats?.totalObjects || 0, icon: Building2, color: 'wp-medium', action: null },
          { label: t('status.working'), value: stats?.totalWorking || 0, icon: Play, color: 'emerald', action: 'working' },
          { label: t('status.onPause'), value: stats?.totalPause || 0, icon: Pause, color: 'amber', action: 'pause' },
          { label: t('status.atLunch'), value: stats?.totalLunch || 0, icon: UtensilsCrossed, color: 'wp-light', action: 'lunch' },
          { label: t('status.outside'), value: stats?.totalOutside || 0, icon: MapPin, color: 'wp-dark', action: 'outside' },
          { label: t('dashboard.violations'), value: stats?.totalViolations || 0, icon: AlertTriangle, color: 'rose', action: 'violation' },
        ].map((stat) => (
          <button
            key={stat.label}
            type="button"
            onClick={() => stat.action && openStatusDialog(stat.action)}
            disabled={!stat.action}
            className={`glass-card rounded-2xl p-5 hover-lift stat-card text-left ${stat.action ? 'cursor-pointer' : 'cursor-default'}`}
            style={{ '--accent-color': `var(--${stat.color}-500)` }}
          >
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-xl bg-${stat.color}-500/20 flex items-center justify-center`}>
                <stat.icon className={`h-5 w-5 text-${stat.color}-400`} />
              </div>
              <div>
                <p className={`text-2xl font-bold font-heading text-${stat.color}-400`}>{stat.value}</p>
                <p className="text-xs text-white/40">{stat.label}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Objects Grid */}
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold text-white font-heading flex items-center gap-3">
          <Zap className="h-6 w-6 text-indigo-400" />
          {t('dashboard.liveObjects')}
        </h2>
        
        {liveData.length === 0 ? (
          <div className="glass-card rounded-2xl p-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-wp-light/20 mb-4" />
            <p className="text-wp-light/40">{t('objects.noObjectsAdmin')}</p>
            <p className="text-wp-light/30 text-sm mt-2">{t('dashboard.noObjectsHint') || 'Create objects in the Objects section to see live data here'}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {liveData.map((obj) => (
              <div key={obj.object_id} className="glass-card rounded-2xl p-6 hover-lift object-card">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-wp-medium/20 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-wp-light" />
                    </div>
                    <h3 className="font-semibold text-white">{obj.object_name}</h3>
                  </div>
                  {obj.total_violations > 0 && (
                    <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-rose-500/20 text-rose-400 text-sm">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      {obj.total_violations}
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { value: obj.total_working, label: t('common.work'), color: 'emerald' },
                    { value: obj.total_pause, label: t('status.pause'), color: 'amber' },
                    { value: obj.total_lunch, label: t('status.lunch'), color: 'cyan' },
                    { value: obj.total_outside, label: t('common.out'), color: 'slate' },
                  ].map((s) => (
                    <div key={s.label} className="text-center">
                      <p className={`text-xl font-bold font-heading text-${s.color}-400`}>{s.value}</p>
                      <p className="text-xs text-white/30">{s.label}</p>
                    </div>
                  ))}
                </div>
                
                <div className="mt-5 pt-4 border-t border-wp-dark/30 flex items-center gap-2 text-wp-light/30 text-sm">
                  <Users className="h-4 w-4" />
                  {obj.employees?.length || 0} {t('employee.assigned')}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={statusDialog.open} onOpenChange={(open) => setStatusDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="w-[calc(100vw-16px)] max-w-lg bg-wp-darker border-wp-dark/50 p-4 md:p-6 max-h-[85vh] overflow-y-auto">
          <DialogTitle className="text-white text-lg font-semibold">
            {statusDialog.title}
          </DialogTitle>
          {statusDialog.employees.length === 0 ? (
            <p className="text-wp-light/40 text-sm mt-3">{t('common.none') || 'None'}</p>
          ) : (
            <div className="mt-4 space-y-3">
              {statusDialog.employees.map((emp) => (
                <div key={`${emp.object_name}-${emp.user_id}`} className="rounded-xl border border-wp-dark/50 bg-wp-dark/20 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-white truncate">{emp.user_name}</p>
                      <p className="text-xs text-wp-light/40 truncate">{emp.user_email}</p>
                    </div>
                    {emp.has_violation && (
                      <AlertTriangle className="h-4 w-4 text-rose-400 shrink-0" />
                    )}
                  </div>
                  <div className="mt-2 text-xs text-wp-light/50">
                    {emp.object_name}
                  </div>
                  <div className="mt-1 text-xs text-white/40">
                    {t(`status.${emp.status}`) || emp.status}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


